import React, { useEffect, useState, useMemo } from 'react';
import styles from '../Style/Satelite.module.scss';
import AltimeterComparison from '../Plot/AltimeterComparison';
import {
  ADVANCED_ANALYTICS,
  DownloadInfoItem,
  IBestDataset,
  IValidation,
} from '../../../../types/analytics.d';
import { IEnrichedMetadata } from '../../../../types/metadata';
import Api from '../../../../api/Api';
import { IAdvancedAnalyticsApiPayload } from '../../../../types/chart';
import { Spin } from 'antd';
import { DATASET_CATEGORIES } from '../../../../utils/filterBestDataset';
import { UserGroupPermissions } from '../../../../types/userGroupPermissions';
import { datasetType, GLOBAL_HD_DTU_ID, SATELLITE_TAB } from '../../../../utils/common';
import useDatasetStore from "../../../../store/datasetStore";
import {usePointStore} from "../../../../store/pointStore";
import useHomeStore from "../../../../store/homeStore";
import useAnalyticsStore from "../../../../store/analyticsStore";

export default function SateliteTab(props: {
  downloadItems: DownloadInfoItem[];
  activePointId: string;
  coordinates: number[];
  previewDatasetId: string;
  userGroupPermissions: UserGroupPermissions;
  pointName: string;
  datasetKey: string
  gridCellSize: string;
  waterDepth: string;
  activePanel: string
  activePlot: string[];
  setActivePlot: React.Dispatch<React.SetStateAction<string[]>>;
  headerSection: any
  onChangeActiveKey: (item: any) => void
}) {
  const enrichedMetadata = useAnalyticsStore(state => state.enrichedMetadata)
  const setEnrichedMetadata = useAnalyticsStore(state => state.setEnrichedMetadata)
  const setIsChangeDatasetWarningVisible = useHomeStore(state => state.setIsChangeDatasetWarningVisible)
  const setTemporaryDatasetName = useHomeStore(state => state.setTemporaryDatasetName)
  const timeStepInterval = useHomeStore(state => state.timeStepInterval)
  const datasetBriefs = useDatasetStore(state => state.datasetBriefs)
  const fwePoints = usePointStore(state => state.fwePoints)
  const category = props.datasetKey
  const [loading, setLoading] = useState(true);
  const [extractionError] = useState('');

  /* TODO: Validation view has been updated to the new UI Design, with the intent of
  having more than one type of Comparison view e.g. 'Scatterometer Comparison', 'In-situ Comparison.

  As the other Comparison data sets are not ready, further implementation for this feature
  is put on-halt */

  const [availableValidation, setAvailableValidation] = useState<IValidation[]>(
    []
  );


  const onDatasetChange = (value: string) => {
    setTemporaryDatasetName(value)
    setIsChangeDatasetWarningVisible(true)
  }

  const isFWE = useMemo(() => {
    const findFwe = datasetBriefs?.find(
      (item) => props.previewDatasetId === item.Id && item.IsFWE
    );

    return !!findFwe;
  }, [props.previewDatasetId, datasetBriefs]);

  const fwePointName = useMemo(() => {
    const findFwePoint = fwePoints?.find(
      (item) => {
        return (
          props.coordinates?.[0] === Number(item.position[0].toFixed(6))
          && props.coordinates?.[1] === Number(item.position[1].toFixed(6))
        )
      }
    )
    return findFwePoint?.name || ''
  }, [props.coordinates, fwePoints])


  useEffect(() => {
    setAvailableValidation([
      {
        id: 'Altimeter Comparison',
        name: 'Altimeter Comparison',
        basic: true,
        postfix: '',
        disabled: false,
      },
    ]);
  }, []);

  useEffect(() => {
    if (props.previewDatasetId && props.downloadItems?.length > 0 && props.activePanel === SATELLITE_TAB) {
      setLoading(true);
      const selectedDatasets = getSelectedDatasets();
      setAltimeterError('');
      setAltimeterImage('');
      setAltimeterLinks([]);
      triggerFullExtraction(props.previewDatasetId, props.coordinates.join());
    }
  }, [props.downloadItems, props.previewDatasetId, props.coordinates, props.activePanel]);

  async function triggerFullExtraction(
    datasetId: string,
    coordinatesString: string
  ) {
    const datasetTask = enrichedMetadata.find(
      (task: IEnrichedMetadata) =>
        task.DatasetId === datasetId &&
        task.CoordinatesString === coordinatesString
    );
    if (datasetTask !== undefined) {
      setEnrichedMetadata(enrichedMetadata);
    } else {
      const metadata = await Api.metadata.getMetadata(datasetId);
      if (metadata) {
        const createFullExtractionTasks =
          await Api.service.createFullExtractionTasks(
            [metadata],
            props.coordinates,
            props.pointName,
            props.gridCellSize,
            props.waterDepth,
            datasetBriefs,
            fwePoints,
            props.previewDatasetId === GLOBAL_HD_DTU_ID ? parseInt(timeStepInterval) : undefined
          );
        const enrichedMDs = enrichedMetadata.concat(
          createFullExtractionTasks
        );
        setEnrichedMetadata(enrichedMDs);
      }
    }

    setLoading(false);
  }

  function getSelectedDatasets() {
    const briefs = datasetBriefs;
    const selectedDatasets = props.downloadItems.map(
      (item: DownloadInfoItem) => {
        const category =
          item.radioKey === 'SW'
            ? DATASET_CATEGORIES.WAVES
            : item.radioKey === 'HD'
            ? DATASET_CATEGORIES.OCEAN
            : DATASET_CATEGORIES.ATMOSPHERE;
        const brief = briefs.find((b) => b.Id === item.datasetId);
        return {
          label: category,
          id: item.datasetId,
          value: brief?.Acronym || category.toString(),
        };
      }
    );
    return selectedDatasets;
  }

  const [altimeterError, setAltimeterError] = useState('');
  const [altimeterImage, setAltimeterImage] = useState('');
  const [altimeterLinks, setAltimeterLinks] = useState<string[]>([]);
  async function getAdvancedAnalyticData(
    payload: IAdvancedAnalyticsApiPayload,
    type: ADVANCED_ANALYTICS
  ) {
    const result = await Api.chart.getAdvancedAnalytics({
      ...payload,
      PointName: isFWE ? fwePointName : payload.PointName,
      IsFwe: isFWE,
      PlotFlag: '[1 3]',
    });
    if (result) {
      if (result.Result && result.Result.img) {
        switch (type) {
          case ADVANCED_ANALYTICS.ALTIMETER_COMPARISON:
            setAltimeterImage('data:image/png;base64,' + result.Result.img);
            setAltimeterLinks(result.Result.links);
            break;
          default:
        }
      } else {
        const errorMessage =
          result.Error && result.Error.ErrorMessage
            ? result.Error.ErrorMessage
            : 'Failed to return data';
        switch (type) {
          case ADVANCED_ANALYTICS.ALTIMETER_COMPARISON:
            setAltimeterImage('')
            setAltimeterError(errorMessage);
            break;
          default:
        }
      }
    }
  }

  const onUpdateAltimeterComparison = (
    payload: IAdvancedAnalyticsApiPayload
  ) => {
    setAltimeterError('');
    setAltimeterImage('');
    setAltimeterLinks([]);
    getAdvancedAnalyticData(payload, ADVANCED_ANALYTICS.ALTIMETER_COMPARISON);
  };

  const datasets = useMemo(() => {
    const selectedDataset: IBestDataset[] = []
    props.downloadItems.forEach(element => {
      const options = element.options.map((item) => {
        return {
          id: item.id,
          label: datasetType[element.radioKey],
          value: item.value
        }
      })
      selectedDataset.push(...options)
    });
    if (selectedDataset.length > 0) {
      return selectedDataset
    }
    return []
  }, [category, props.downloadItems, props.coordinates])

  const renderAltimeterComparison = () => {
    if (props.activePlot.includes('Altimeter Comparison') && enrichedMetadata) {
      return (
        <AltimeterComparison
          altimeterComparisonImage={altimeterImage}
          coordinates={props.coordinates}
          datasets={datasets}
          enrichedMetadata={enrichedMetadata}
          error={altimeterError}
          onUpdate={onUpdateAltimeterComparison}
          previewDatasetId={props.previewDatasetId}
          altimeterComparisonLinks={altimeterLinks}
          previewDatasetTimeRange={
            props.userGroupPermissions?.validationDateRange
          }
          pointName={props.pointName}
          activePanel={props.activePanel}
          headerSection={props.headerSection}
          datasetChange={onDatasetChange}
          onChangeActiveKey={props.onChangeActiveKey}
        />
      );
    }
    return null;
  };

  return (
    <>
      {loading ? (
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
          <Spin size="large" />
        </div>
      ) : extractionError !== '' ? (
        <div>{extractionError}</div>
      ) : (
        <div className={styles.chartWrapper} style={{height: '100%'}}>{renderAltimeterComparison()}</div>
      )}
    </>
  );
}
